import { auth, db } from '../firebase';
import {
  collection, deleteDoc, doc, getDoc, getDocs, serverTimestamp, setDoc,
} from 'firebase/firestore';
import { PublicProfileService } from './PublicProfileService';

function requireUid() {
  const uid = auth?.currentUser?.uid;
  if (!uid || !db) throw new Error('Must be signed in');
  return uid;
}

export const FriendsService = {
  /** Looks up a user by their shareable invite code and sends them a friend request. */
  async sendRequestByCode(code) {
    const uid = requireUid();
    const target = await PublicProfileService.findByInviteCode(code);
    if (!target) return { ok: false, reason: 'not_found' };
    if (target.uid === uid) return { ok: false, reason: 'self' };

    const existingFriend = await getDoc(doc(db, 'users', uid, 'friends', target.uid));
    if (existingFriend.exists()) return { ok: false, reason: 'already_friends' };

    await setDoc(doc(db, 'users', target.uid, 'friendRequests', uid), {
      fromUid: uid,
      fromName: auth.currentUser.displayName || 'Learner',
      sentAt: serverTimestamp(),
    });
    return { ok: true, target };
  },

  async getIncomingRequests() {
    const uid = requireUid();
    const snap = await getDocs(collection(db, 'users', uid, 'friendRequests'));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  async acceptRequest(fromUid) {
    const uid = requireUid();
    const addedAt = serverTimestamp();
    await Promise.all([
      setDoc(doc(db, 'users', uid, 'friends', fromUid), { addedAt }),
      setDoc(doc(db, 'users', fromUid, 'friends', uid), { addedAt }),
      deleteDoc(doc(db, 'users', uid, 'friendRequests', fromUid)),
    ]);
  },

  async declineRequest(fromUid) {
    const uid = requireUid();
    await deleteDoc(doc(db, 'users', uid, 'friendRequests', fromUid));
  },

  async removeFriend(friendUid) {
    const uid = requireUid();
    await Promise.all([
      deleteDoc(doc(db, 'users', uid, 'friends', friendUid)),
      deleteDoc(doc(db, 'users', friendUid, 'friends', uid)),
    ]);
  },

  /** Friend list enriched with each friend's public leaderboard-visible stats. */
  async getFriends() {
    const uid = requireUid();
    const snap = await getDocs(collection(db, 'users', uid, 'friends'));
    const profiles = await Promise.all(
      snap.docs.map((d) => PublicProfileService.get(d.id)),
    );
    return profiles.filter(Boolean);
  },
};
